import { produce } from "immer";
import { Action, ActionType, handlers } from "./storeActions";
import { StoreSchema } from "./StoreSchema";
import { toaster } from "../toaster";
import { useEffect, useState } from "react";
import { apiReq } from "../apiReq";
import { UserState } from "@greenflagdate/shared";
import { ReqMethod } from "@larner.dev/req";

declare global {
  interface Window {
    gfStore: Store;
  }
}

export const LOGIN_TOKEN_KEY = "GF_TOKEN";

const generateEmptyStore = (): StoreSchema => {
  const token = localStorage.getItem(LOGIN_TOKEN_KEY);

  // Check for transfer token in URL (from native app external browser)
  const urlParams = new URLSearchParams(window.location.search);
  const transferToken = urlParams.get("tt");

  if (token || transferToken) {
    console.log("has token or transferToken");
    apiReq.headers.authorization = `Bearer ${token}`;
    (async () => {
      let user;
      if (token) {
        try {
          const userState = await apiReq.standardRequest<UserState>(
            "/user/state",
            ReqMethod.GET
          );
          user = userState?.user;
          if (userState) {
            store.dispatch({
              type: ActionType.UpdateUserState,
              params: userState,
            });
          } else {
            store.dispatch({
              type: ActionType.LogOut,
              params: {},
            });
          }
        } catch (error) {
          console.log(error);
          store.dispatch({
            type: ActionType.LogOut,
            params: {},
          });
        }
      }

      if (!user && transferToken) {
        // Exchange transfer token for regular session
        try {
          const response = await apiReq.standardRequest<UserState & {
            token: string;
          }>("/user/exchange-token", ReqMethod.POST, {
            transferToken,
          });

          store.dispatch({
            type: ActionType.Login,
            params: response,
          });
        } catch (error) {
          console.error("Failed to exchange transfer token:", error);
        }
        // Remove invalid transfer token from URL
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete("tt");
        window.history.replaceState({}, "", newUrl);
      }

      // Set loading to false now that all async operations are complete
      store.dispatch({
        type: ActionType.SetLoading,
        params: {
          loading: false,
        },
      });
    })();
  }

  return {
    loading: !!(token || transferToken),
    auth: {
      token: token || undefined,
    },
    profileQueue: [],
  };
};

class Store extends EventTarget {
  private privateData: StoreSchema = generateEmptyStore();
  dispatch(action: Action) {
    let result: unknown = null;
    const newData = produce(this.privateData, (draft) => {
      try {
        // @ts-expect-error we know that action.type should accept action.params but typescript does not
        result = handlers[action.type](action.params, draft);
      } catch (e) {
        console.trace(e);
        const title =
          e instanceof Error
            ? e.message
            : typeof e === "string"
              ? e
              : String(e);
        toaster.create({ type: "error", title });
      }
    });
    if (this.privateData !== newData) {
      this.privateData = newData;
      this.dispatchEvent(
        new CustomEvent("update", { detail: { store: newData } })
      );
    }
    return result;
  }

  get data(): StoreSchema {
    return Object.freeze(this.privateData);
  }
}

export const store = new Store();

window.gfStore = store;

// Re-fetch /user/state and merge the result into the store. Call after any
// action that invalidates matches / sparks / user-level data (e.g. unmatch)
// so UI reading from the store refreshes without a manual page reload.
export async function refreshUserState(): Promise<void> {
  try {
    const userState = await apiReq.standardRequest<UserState>(
      "/user/state",
      ReqMethod.GET
    );
    if (userState) {
      store.dispatch({
        type: ActionType.UpdateUserState,
        params: userState,
      });
    }
  } catch (error) {
    console.warn("refreshUserState failed", error);
  }
}

export const useStoreData = () => {
  const [storeData, setStoreData] = useState(store.data);
  useEffect(() => {
    const updateHandler: EventListener = (updateEvent) => {
      setStoreData(
        (updateEvent as CustomEvent<{ store: StoreSchema }>).detail.store
      );
    };
    store.addEventListener("update", updateHandler);
    setStoreData(store.data);
    return () => {
      store.removeEventListener("update", updateHandler);
    };
  }, []);
  return storeData;
};
